import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Download, Menu, MoreHorizontal } from "lucide-react";
import { parseSongInfo } from "@/utils/songUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSidebar } from "@/components/ui/sidebar";
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { toast } from 'sonner';

interface SessionHeaderProps {
  filename: string;
  sessionId: string;
  onDelete: () => void;
  onDownloadAll: () => void;
  showDownload: boolean;
}

export const SessionHeader = ({ 
  filename, 
  sessionId,
  onDelete, 
  onDownloadAll, 
  showDownload 
}: SessionHeaderProps) => {
  const queryClient = useQueryClient();
  const songInfo = parseSongInfo(filename);
  const isMobile = useIsMobile();
  const { toggleSidebar } = useSidebar();
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [newName, setNewName] = useState(filename);

  useEffect(() => {
    setNewName(filename);
  }, [filename]);

  // Setup mutation observer to watch for style changes on body
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const body = document.body;
          if (body.style.pointerEvents === 'none' && !isRenameModalOpen && !isDeleteModalOpen) {
            body.style.removeProperty('pointer-events');
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, [isRenameModalOpen, isDeleteModalOpen]);

  // Clean up pointer-events when modals close
  useEffect(() => {
    if (!isRenameModalOpen && !isDeleteModalOpen) {
      // Wait for animation to complete
      setTimeout(() => {
        document.body.style.removeProperty('pointer-events');
      }, 300); // 300ms matches the modal animation duration
    }
  }, [isRenameModalOpen, isDeleteModalOpen]);

  const handleRename = async () => {
    if (!newName.trim()) {
      toast.error('Please enter a name');
      return;
    }

    try {
      await supabase
        .from('stem_sessions')
        .update({ original_filename: newName })
        .eq('id', sessionId);
      
      await queryClient.invalidateQueries({ queryKey: ['stemSessions'] });
      toast.success('Session renamed successfully');
    } catch (error) {
      console.error('Error renaming session:', error);
      toast.error('Failed to rename session');
    } finally {
      setIsRenameModalOpen(false);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete();
    } finally {
      setIsDeleteModalOpen(false);
    }
  };

  const handleDawClick = () => {
    posthog.capture('DAW CTA Clicked', {
      songTitle: songInfo.title,
      artist: songInfo.artist
    });
    window.open('http://sesh.fm/product-tour', '_blank');
  };
  
  return (
    <div className={`flex ${isMobile ? 'flex-col space-y-4' : 'justify-between'} items-start md:items-center mb-6`}>
      <div className="flex items-center gap-2 w-full">
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 flex items-center justify-center aspect-square"
            onClick={toggleSidebar}
          >
            <Menu className="h-4 w-4" />
          </Button>
        )}
        <div>
          <h2 className="text-xl md:text-2xl text-foreground">{songInfo.title}</h2>
          <p className="text-muted-foreground">{songInfo.artist}</p>
        </div>
      </div>
      <div className="flex gap-2 w-full md:w-auto">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="icon" className="h-10 w-10">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => setIsRenameModalOpen(true)}>
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-destructive"
              onSelect={() => setIsDeleteModalOpen(true)}
            >
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {showDownload && (
          <Button
            onClick={onDownloadAll}
            variant="outline"
            className="h-10 px-4 flex items-center gap-2 flex-1 md:flex-none"
          >
            <Download className="w-4 h-4" />
            <span className="hidden md:inline">Download All</span>
            <span className="md:hidden">Download</span>
          </Button>
        )}
        <Button
          variant="default"
          className="bg-[#3B82F6] hover:bg-[#60A5FA] text-white h-10 px-4 flex-1 md:flex-none"
          onClick={handleDawClick}
        >
          <img 
            src="/lovable-uploads/1f60214c-efc7-49df-9a80-67c09b9772bf.png" 
            alt="Sesh Logo" 
            className="w-4 h-4 mr-2"
          />
          <span className="hidden md:inline">Open in Sesh</span>
          <span className="md:hidden">Open in Sesh</span>
        </Button>
      </div>

      <Dialog open={isRenameModalOpen} onOpenChange={setIsRenameModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rename Session</DialogTitle>
            <DialogDescription>
              Enter a new name for this session.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter new name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRenameModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleRename}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this session and all its stems.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setIsDeleteModalOpen(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
